import React, { useContext, useEffect, useState } from 'react';
import './solutions-detail.scss';
import { useParams } from "react-router-dom";
import { AppContext } from '../../services/translationContext';
import { Link } from "react-router-dom";
import { VectorAnimated } from '../../components/vector-animated/vector-animated';
import DOMPurify from 'dompurify';
import { Loader } from '../../components/loader/loader';
import { Helmet } from 'react-helmet-async';
import SeoHelmet from '../../components/SeoHelmet';
import { FlatCard } from '../../components/cards/flat-card';
import parse from 'html-react-parser';

const api = process.env.REACT_APP_URL_API;

export const SolutionsDetail = () => {
    const text_test1 = `
<section id='come-funziona' class='section-dark-solution'><div class='section-inner-solution'><p class='section-label-solution'>Features</p><h2>It does not suggest. It acts.</h2><p class='section-intro-solution'>The SAP Agent connects directly to the system, reads source code, executes operations and fixes code — without stopping until the result is valid. </p><div class='cap-grid-solution reveal-solution'><div class='cap-card-solution'><span class='cap-tag-solution'>dump</span><div class='cap-title-solution'>Dump analysis and resolution</div><p class='cap-desc-solution'>The agent reads the dump from ST22, identifies the root cause, suggests the fix and applies it directly to the ABAP source code. If activation generates new errors, it self-corrects until the program reaches a working state. </p></div><div class='cap-card-solution'><span class='cap-tag-solution'>review</span><div class='cap-title-solution'>Code review</div><p class='cap-desc-solution'>Analyzes ABAP programs across multiple dimensions: quality, bugs, security and readability. Produces structured reports with priorities and fix recommendations. Replaces commercial tools with licenses costing hundreds of thousands of euros. </p></div><div class='cap-card-solution'><span class='cap-tag-solution'>security</span><div class='cap-title-solution'>Security analysis</div><p class='cap-desc-solution'>Identifies vulnerabilities in SAP code with a level of detail comparable to specialized market tools. Based on custom skills internally developed by Sydea. </p></div><div class='cap-card-solution'><span class='cap-tag-solution'>docs</span><div class='cap-title-solution'>Technical documentation</div><p class='cap-desc-solution'>Starting from a program or an entire package, it generates structured and readable technical documentation, including logical flow and dependencies between objects. </p></div><div class='cap-card-solution'><span class='cap-tag-solution'>dev</span><div class='cap-title-solution'>Development from specifications</div><p class='cap-desc-solution'>Given a set of functional specifications, it builds a development plan, generates classes and objects, activates them in the system and autonomously manages compilation errors. Used daily by Sydea on real developments. </p></div><div class='cap-card-solution'><span class='cap-tag-solution'>skills</span><div class='cap-title-solution'>Custom skills</div><p class='cap-desc-solution'>The system uses specialized instructions built by Sydea that automatically activate based on the request context. The operator can interact using natural language: the system understands and decides how to proceed. </p></div></div></div></section><section class='terminal-box-solution'><div class='terminal'><div class='terminal-bar'><div class='dot dot-r'></div><div class='dot dot-y'></div><div class='dot dot-g'></div><span class='terminal-title'>claude — SAP Agent</span></div><div class='terminal-body'><span class='t-line'><span class='t-prompt'>$</span><span class='t-cmd'>claude 'analyzes the ZDEMO_ORDER program dump'</span></span><span class='t-blank'></span><span class='t-line t-out'>Connecting to SAP system...</span><span class='t-line t-out'>Reading ST22 dump...</span><span class='t-blank'></span><span class='t-line t-highlight'>Root cause identified:</span><span class='t-line t-out'>Null pointer dereference</span><span class='t-line t-out'>line 47 — uninitialized field</span><span class='t-blank'></span><span class='t-line t-out'>Applying fix to ABAP source code...</span><span class='t-line t-out'>Activating program...</span><span class='t-blank'></span><span class='t-line t-success'>Program activated successfully.</span><span class='t-line t-out'>No compilation errors.</span><span class='t-blank'></span><span class='t-line'><span class='t-prompt'>$</span><span class='cursor'></span></span></div></div></section><section class='section-light-solution'><div class='section-inner-narrow-solution'><p class='section-label-solution'>How it works</p><div class='how-grid-solution reveal-solution'><div><h2>Connected to the system. Not to static code.</h2><ul class='how-steps'><li class='how-step-solution'><span class='how-num-solution'>01</span><div class='how-content-solution'><strong>Connection via MCP</strong><p>The agent connects to the SAP system through an MCP server. It can read source code, search dumps, create objects, trigger activations — all within the live environment.</p></div></li><li class='how-step-solution'><span class='how-num-solution'>02</span><div class='how-content-solution'><strong>Context awareness</strong><p>Before writing code, the agent checks what already exists in the system: CDS views, BAPIs, table structures. Nothing is invented.</p></div></li><li class='how-step-solution'><span class='how-num-solution'>03</span><div class='how-content-solution'><strong>Execution and self-correction</strong><p>When it activates code and encounters compilation errors, it reads the error message and fixes itself — exactly as a developer would, but automatically.</p></div></li><li class='how-step-solution'><span class='how-num-solution'>04</span><div class='how-content-solution'><strong>Role-specific models</strong><p>For complex reasoning — architectures and development plans — it uses top-tier AI models. For code generation and execution, it switches to faster models.</p></div></li></ul></div><div class='how-aside-solution'><div class='how-aside-solution-label'>In production</div><div class='how-aside-solution-quote'>We automatically develop dozens of classes and objects. I use it every day on complex programs that typically take days or even weeks to complete.</div><p class='how-aside-solution-note'>The SAP Agent is not a proof of concept: it is the tool Sydea uses for development today. The trade show demos showcase simplified scenarios, but the system runs on real-world projects of medium and high complexity.</p></div></div></div></section><section class='section-dark-solution'><div class='section-inner-solution-narrow'><p class='section-label-solution'>The difference that matters</p><h2>Why an agent, not an assistant.</h2><div class='distinction reveal-solution'><div class='dist-col-solution bad'><div class='dist-label-solution'>Traditional AI Assistant</div><div class='dist-title-solution'>Suggests.</div><p class='dist-text-solution'>It generates code based on static knowledge. It may hallucinate non-existent APIs, incorrect table structures, or BAPIs with invalid fields. The result must be manually reviewed and corrected.</p></div><div class='dist-col-solution good'><div class='dist-label-solution'>Sydea SAP Agent</div><div class='dist-title-solution'>Acts.</div><p class='dist-text-solution'>It connects to the system, verifies that objects exist and match the expected structure, writes correct code because it already knows what is there — and doesn't stop until the program is active and working.</p></div></div></div></section><section id='contatti' class='section-cta-solution'><div class='section-inner-solution'><h2>Want to see how it fits into your SAP environment?</h2><p>Let's discuss how the SAP Agent can become part of your development process.</p><a href='mailto:info@sydea.com' class='cta-btn'>Contact us</a></div></section>
    `;

    let { solution_id } = useParams();
    let { lang } = useParams();

    const { services: {TranslationsService} } = useContext(AppContext);

    const [showLoader, setShowLoader] = useState(false);
    const [listClientStories, setListClientStories] = useState([]);
    const [solution, setSolution] = useState({});

    useEffect(() => {
      const solutionUrl = TranslationsService
        .labels('solutions.items')
        .find((x) => x.id === solution_id);

      setSolution(solutionUrl || {});
    }, [solution_id, TranslationsService]);

    useEffect(() => {
      if (!solution.fulltext) return;

      const reveals = document.querySelectorAll(".reveal-solution");

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
        }
      );

      reveals.forEach((el) => observer.observe(el));

      return () => observer.disconnect();
    }, [solution.fulltext]);
  
 

  return (
    <>
    <SeoHelmet 
      title={solution['meta-title']}
      description={solution['meta-description']}
      image={solution.hero}
      schema={
        [
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: solution.title,
            description: solution["meta-description"],
            url: `https://www.sydea.com/${lang}/solutions/${solution.id}`,
            applicationCategory: solution.info?.applicationCategory,
            applicationSubCategory: solution.info?.applicationSubCategory,
            operatingSystem: solution.info?.operatingSystem,
            inLanguage: lang === 'it' ? 'it-IT' : 'en-US',
            provider: {
              "@id": "https://www.sydea.com/#organization"
            },
            ...(solution.info?.areaServed && {
              areaServed: Array.isArray(solution.info.areaServed)
                ? solution.info.areaServed.map(area => ({ "@type": "Country", name: area }))
                : { "@type": "Country", name: solution.info.areaServed }
            }),
            ...(solution.info?.features && {
              featureList: solution.info.features
            }),
            // provider: SYDEA_ORGANIZATION_SCHEMA,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: lang === 'it' ? 'Home' : 'Home',
                item: `https://www.sydea.com/${lang}`
              },
              {
                "@type": "ListItem",
                position: 2,
                name: lang === 'it' ? 'Soluzioni' : 'Solutions',
                item: `https://www.sydea.com/${lang}/solutions`
              },
              {
                "@type": "ListItem",
                position: 3,
                name: solution.title,
                item: `https://www.sydea.com/${lang}/solutions/${solution.id}`
              }
            ]
          }
        ]
      }
    />
    <div className="section-home light">
      {/* <div className='syd-background-solution' style={{backgroundImage: solution.hero ? `url(${solution.hero})` : 'none'}}></div> */}
      <div className='syd-background-solution' style={{backgroundImage: solution.hero ? `url(${solution.hero})` : 'none'}}>
        {solution.hero && (
          <img
            src={solution.hero}
            alt={`${solution.title} – ${solution.det_subtitle || solution.subtitle}`}
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0
            }}
            loading="lazy"
          />
        )}
      </div>
      <div style={{ position: 'relative', zIndex: 2 }}>

        <section className="hero">
          <div className="hero-content">
            <p className='dark-mode-text m-0 breadcrumb-detail'>
              <Link to={`/${lang}`} className='text-deco-none dark-mode-text transition-03s-eio'>Home</Link>
              &nbsp;&#9656;&nbsp;
              <Link to={`/${lang}/solutions`} className='text-deco-none dark-mode-text transition-03s-eio'>{TranslationsService.labels(`menu.solutions.label`)}</Link>
            </p>
              <div className="hero-text">
                  {/* <h1 className="hero-title" style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <div className='logo-solutions' aria-label={`${solution.title} logo`}>{solution?.svg_logo ? parse(solution.svg_logo) : null}</div>
                    {solution.id !== 'sap-intelligence' && <span className="highlight">{solution.title}</span>}
                  </h1> */}
                  <h1 className="hero-title" style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <div className='logo-solutions' aria-label={`${solution.title} logo`}>
                      {solution?.svg_logo ? parse(solution.svg_logo) : null}
                    </div>
                    {solution.id !== 'sap-intelligence' && <span className="highlight">{solution.title}</span>}
                    {solution.det_subtitle && (
                      <span className="visually-hidden"> – {solution.det_subtitle}</span>
                    )}
                  </h1>
                  <h2 className="hero-subtitle">
                      {solution.det_subtitle}
                  </h2>
                  <p className="hero-description">
                    {solution.hero_desc ? parse(solution.hero_desc) : null}
                  </p>
                  <div className="hero-cta-group">
                      <a href="#contatti" className="cta-primary">
                          {solution.cta_primary}
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                      </a>
                      <a href="#come-funziona" className="cta-secondary">{solution.cta_secondary}</a>
                  </div>
              </div>
            </div>
        </section>

        <div>
          {solution.fulltext ? parse(solution.fulltext) : null}
          {/* {solution.fulltext ? parse(text_test1) : null} */}
          
        </div>

      </div>
    </div>
    </>
  );
};